if (!customElements.get('lunicora-product-gifts-controller')) {
  /*
    Product-options apps are read through the shared lunicora-easify-options
    reader (loaded from theme.liquid) so the gift carousel, the cart drawer
    picker and the product page agree on every option and its required flag.
  */
  const getEasifyCustomOptions = (product) => (
    window.LunicoraEasify ? window.LunicoraEasify.getCustomOptions(product) : []
  );
  const hasExternalPersonalizer = (product) => (
    window.LunicoraEasify ? window.LunicoraEasify.hasCustomilyPersonalizer(product) : Promise.resolve(false)
  );

  class LunicoraProductGiftsController extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gift-track]');
      this.previousButton = this.querySelector('[data-gift-prev]');
      this.nextButton = this.querySelector('[data-gift-next]');

      if (!this.track) return;

      // Without this guard an unpaired second connectedCallback (a re-injected
      // inline mover script, a theme-editor re-render) would stack a duplicate
      // mobile picker on document.body and orphan the handler Maps.
      if (this.dataset.giftControllerReady === 'true') return;
      this.dataset.giftControllerReady = 'true';

      this.onScroll = () => {
        if (window.matchMedia('(min-width: 750px)').matches) {
          this.closeVariantPickers();
        }
        this.updateButtons();
        this.updateDots();
      };
      this.onResize = () => {
        this.updateButtons();
        this.updateDots();
      };
      this.onPrevious = () => this.move(-1);
      this.onNext = () => this.move(1);
      this.variantButtonHandlers = new Map();
      this.requiredOptionHandlers = new Map();
      this.nameInputHandlers = new Map();

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.previousButton?.addEventListener('click', this.onPrevious);
      this.nextButton?.addEventListener('click', this.onNext);
      window.addEventListener('resize', this.onResize, { passive: true });

      this.applyCustomOptions();
      this.setupVariantPickers();
      this.setupMobilePicker();
      this.setupPickerLinks();
      this.checkPersonalizers();
      this.shuffleFallbackItems();
      this.setupAutoplay();

      requestAnimationFrame(() => {
        this.updateButtons();
        this.updateDots();
      });
    }

    disconnectedCallback() {
      if (!this.track) return;
      if (this.onPickerLinkClick) this.removeEventListener('click', this.onPickerLinkClick);
      this.track.removeEventListener('scroll', this.onScroll);
      this.previousButton?.removeEventListener('click', this.onPrevious);
      this.nextButton?.removeEventListener('click', this.onNext);
      window.removeEventListener('resize', this.onResize);
      this.variantButtonHandlers?.forEach((handler, button) => {
        button.removeEventListener('click', handler, true);
      });
      this.variantButtonHandlers?.clear();
      this.requiredOptionHandlers?.forEach((handler, control) => {
        control.removeEventListener('change', handler);
      });
      this.requiredOptionHandlers?.clear();
      this.nameInputHandlers?.forEach((handler, control) => {
        control.removeEventListener('input', handler);
      });
      this.nameInputHandlers?.clear();
      this.giftErrorTimers?.forEach((timer) => clearTimeout(timer));
      this.giftErrorTimers?.clear();
      this.mobilePicker?.remove();
      this.mobilePicker = null;
      this.mobilePickerState = null;
      this.teardownAutoplay();
      delete this.dataset.giftControllerReady;
    }

    move(direction) {
      if (window.matchMedia('(min-width: 750px)').matches) {
        this.closeVariantPickers();
      }

      const firstItem = this.querySelector('.lunicora-product-gifts__item');
      const trackStyles = getComputedStyle(this.track);
      const itemGap = Number.parseFloat(trackStyles.columnGap) || Number.parseFloat(trackStyles.gap) || 0;
      const distance = firstItem ? firstItem.getBoundingClientRect().width + itemGap : this.track.clientWidth;

      this.track.scrollBy({
        left: distance * direction,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    }

    shuffleFallbackItems() {
      // Only the cards topped up from the store-wide best sellers shuffle. They
      // always sit after the same-name cards, so re-appending them in a random
      // order leaves the type-alternating ordering Liquid built untouched.
      if (!this.track) return;

      const items = [...this.track.querySelectorAll('.lunicora-product-gifts__item[data-gift-fallback]')];
      if (items.length < 2) return;

      for (let index = items.length - 1; index > 0; index -= 1) {
        const swapIndex = Math.floor(Math.random() * (index + 1));
        [items[index], items[swapIndex]] = [items[swapIndex], items[index]];
      }

      items.forEach((item) => this.track.appendChild(item));
      this.track.scrollLeft = 0;
      requestAnimationFrame(() => this.updateButtons());
    }

    setupMobilePicker() {
      this.mobilePicker = document.createElement('div');
      this.mobilePicker.className = 'lunicora-variant-picker lunicora-product-gifts-picker';
      this.mobilePicker.hidden = true;
      this.mobilePicker.innerHTML = `
        <button type="button" class="lunicora-variant-picker__backdrop" data-gift-mobile-close aria-label="Close product options"></button>
        <section class="lunicora-variant-picker__dialog" role="dialog" aria-modal="true" aria-labelledby="LunicoraProductGiftPickerTitle" tabindex="-1">
          <div class="lunicora-variant-picker__handle" aria-hidden="true"></div>
          <button type="button" class="lunicora-variant-picker__close" data-gift-mobile-close aria-label="Close product options">
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M5 5l14 14M19 5L5 19" fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="1.8"/></svg>
          </button>
          <div class="lunicora-variant-picker__product">
            <div class="lunicora-variant-picker__media"><img data-gift-mobile-image alt="" width="144" height="144"></div>
            <div class="lunicora-variant-picker__summary">
              <h3 id="LunicoraProductGiftPickerTitle" class="lunicora-variant-picker__title" data-gift-mobile-title></h3>
              <div class="lunicora-variant-picker__prices"><span data-gift-mobile-price></span><s data-gift-mobile-compare hidden></s></div>
              <p class="lunicora-variant-picker__status" data-gift-mobile-status role="status">Choose all options</p>
            </div>
          </div>
          <div class="lunicora-variant-picker__options" data-gift-mobile-options></div>
          <div class="lunicora-variant-picker__name-field" data-gift-mobile-name-field hidden>
            <label for="LunicoraProductGiftPickerName">Name <span>(Optional)</span></label>
            <div class="lunicora-variant-picker__name-input-wrap">
              <input id="LunicoraProductGiftPickerName" type="text" maxlength="12" placeholder="Example: Kevin" autocomplete="off" autocorrect="off" autocapitalize="off" data-gift-mobile-name>
              <span data-gift-mobile-name-count aria-hidden="true">0/12</span>
            </div>
          </div>
          <button type="button" class="lunicora-variant-picker__submit" data-gift-mobile-submit disabled>Add to cart</button>
        </section>
      `;
      document.body.appendChild(this.mobilePicker);

      this.mobilePicker.addEventListener('click', (event) => {
        if (event.target.closest('[data-gift-mobile-close]')) {
          this.closeMobilePicker();
          return;
        }

        const mobileTrigger = event.target.closest('[data-gift-mobile-trigger]');
        if (mobileTrigger) {
          const index = mobileTrigger.dataset.giftMobileTrigger;
          const menu = this.mobilePicker.querySelector(`[data-gift-mobile-menu="${index}"]`);
          const dialogRect = this.mobilePicker.querySelector('.lunicora-variant-picker__dialog')?.getBoundingClientRect();
          const triggerRect = mobileTrigger.getBoundingClientRect();
          const menuHeight = Math.min(menu?.querySelectorAll('button').length * 48 || 0, 270);
          const spaceBelow = dialogRect ? dialogRect.bottom - triggerRect.bottom - 8 : menuHeight;
          const spaceAbove = dialogRect ? triggerRect.top - dialogRect.top - 8 : 0;
          const openUp = dialogRect && spaceBelow < menuHeight && spaceAbove > spaceBelow;
          const availableSpace = openUp ? spaceAbove : spaceBelow;
          menu?.setAttribute('data-gift-mobile-menu-placement', openUp ? 'up' : 'down');
          if (menu) menu.style.maxHeight = `${Math.max(7.2 * 16, Math.min(menuHeight, availableSpace))}px`;
          this.mobilePicker.querySelectorAll('[data-gift-mobile-menu]').forEach((candidate) => {
            const isTarget = candidate === menu;
            candidate.hidden = !isTarget || !candidate.hidden;
            const trigger = this.mobilePicker.querySelector(`[data-gift-mobile-trigger="${candidate.dataset.giftMobileMenu}"]`);
            trigger?.setAttribute('aria-expanded', String(isTarget && !candidate.hidden));
          });
          return;
        }

        const mobileOption = event.target.closest('[data-gift-mobile-option]');
        if (mobileOption) {
          const state = this.mobilePickerState;
          const index = Number(mobileOption.dataset.giftMobileOption);
          const original = state?.controls[index];
          if (!original || mobileOption.disabled) return;

          original.value = mobileOption.value;
          original.dispatchEvent(new Event('change', { bubbles: true }));
          requestAnimationFrame(() => this.syncMobilePicker());
          return;
        }

        if (event.target.closest('[data-gift-mobile-submit]')) {
          const state = this.mobilePickerState;
          if (!state || !this.isMobilePickerComplete(state)) return;
          const variant = this.getMobilePickerVariant(state);
          if (!variant?.available || !this.syncMobilePickerVariant(state, variant)) return;

          void this.addGiftToCart(state.item, state.button, variant);
          this.closeMobilePicker({ returnFocus: false, keepVariantState: true });
        }
      });

      // The sheet lives on document.body, so addGiftToCart cannot see it. Write
      // straight through to the card's own input, the same way option buttons do.
      this.mobilePicker.addEventListener('input', (event) => {
        if (!event.target.closest('[data-gift-mobile-name]')) return;
        const target = this.mobilePickerState?.item.querySelector('[data-gift-name-input]');
        if (target) {
          target.value = event.target.value;
          target.dispatchEvent(new Event('input', { bubbles: true }));
          this.updateGiftNameCount(target);
        }
        this.updateMobileNameCount();
        this.refreshMobileSubmit();
      });

      this.mobilePicker.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeMobilePicker();
        }
      });
    }

    updateGiftNameCount(input) {
      if (!input) return;
      const max = Number.parseInt(input.dataset.giftNameMax || input.maxLength || '12', 10);
      const counter = input.parentElement?.querySelector('[data-gift-name-count]');
      if (counter) counter.textContent = `${input.value.length}/${max}`;
    }

    updateMobileNameCount() {
      const input = this.mobilePicker?.querySelector('[data-gift-mobile-name]');
      const counter = this.mobilePicker?.querySelector('[data-gift-mobile-name-count]');
      if (!input || !counter) return;
      counter.textContent = `${input.value.length}/${input.maxLength}`;
    }

    syncMobileNameField(item) {
      const field = this.mobilePicker?.querySelector('[data-gift-mobile-name-field]');
      const input = this.mobilePicker?.querySelector('[data-gift-mobile-name]');
      if (!field || !input) return;

      const source = item?.querySelector('[data-gift-name-input]');
      field.hidden = !source;
      if (!source) return;

      input.maxLength = Number.parseInt(source.dataset.giftNameMax || '12', 10);
      input.value = source.value || '';
      input.placeholder = source.placeholder || 'Example: Kevin';
      const label = field.querySelector('label');
      if (label) {
        const required = source.hasAttribute('data-gift-required-option');
        label.replaceChildren('Your name ', Object.assign(document.createElement('span'), { textContent: required ? '*' : '(Optional)' }));
      }
      this.updateMobileNameCount();
    }

    isMobilePickerComplete(state = this.mobilePickerState) {
      if (!state) return false;
      const requiredControls = state.requiredControls
        || state.controls.filter((control) => control.hasAttribute('data-gift-required-option'));
      const selectedVariant = this.getMobilePickerVariant(state);
      return requiredControls.every((control) => String(control.value || '').trim() !== '')
        && Boolean(selectedVariant?.available);
    }

    formatMobilePickerMoney(cents) {
      const amount = Number(cents || 0) / 100;
      const currency = window.Shopify?.currency?.active || 'USD';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency,
        }).format(amount);
      } catch (_error) {
        return `$${amount.toFixed(2)}`;
      }
    }

    getMobilePickerVariant(state) {
      if (!state) return null;
      // A single-variant gift has no dropdowns, so its id comes straight off the
      // card. The sheet still opens for it because it offers a name box.
      if (!state.nativeControls?.length) return this.getDefaultGiftVariant(state.item, state.button);
      if (!state.variants?.length) return null;
      const selections = state.nativeControls.map((control) => control.value);
      return state.variants.find((variant) => (
        selections.every((value, index) => variant.options?.[index] === value)
      )) || null;
    }

    syncMobilePickerVariant(state, variant = this.getMobilePickerVariant(state)) {
      if (!state || !variant?.id) return false;

      const variantId = String(variant.id);
      const form = this.getGiftForm(state.item, state.button);
      const variantInput = form?.querySelector('input[name="id"]');
      const upsell = state.item.querySelector('[data-handle]');

      if (variantInput) variantInput.value = variantId;
      if (upsell) upsell.dataset.id = variantId;
      return true;
    }

    getGiftForm(item, button) {
      const formId = button?.getAttribute('form');
      return (formId ? document.getElementById(formId) : null)
        || button?.form
        || item.querySelector('product-form form');
    }

    getDefaultGiftVariant(item, button) {
      const upsell = item.querySelector('[data-id]');
      const form = this.getGiftForm(item, button);
      // The theme's product form can inherit the main page variant when it is
      // rendered inside this custom handle loop. data-id is rendered directly
      // from the card's own product and is therefore the authoritative ID.
      const id = upsell?.dataset.id || form?.querySelector('input[name="id"]')?.value;
      if (!id) return null;

      return {
        id,
        product_id: upsell?.dataset.productId || '',
        available: !button.disabled,
      };
    }

    getGiftOptionName(control) {
      const match = String(control?.name || '').match(/\[([^\]]+)\]/);
      return (match?.[1] || control?.getAttribute('aria-label') || '').trim();
    }

    async prefillGiftVariantFromCart(item, picker, button, requiredControls = []) {
      const nativeControls = [...picker.querySelectorAll('select')];
      if (!nativeControls.length) return null;

      let variants = [];
      try {
        variants = JSON.parse(picker.querySelector('script[type="application/json"]')?.textContent || '[]');
      } catch (_error) {
        variants = [];
      }
      if (!variants.length) return null;

      let cart;
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart.js`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return null;
        cart = await response.json();
      } catch (_error) {
        return null;
      }

      const normalize = (value) => String(value || '').trim().toLowerCase();
      const nativeOptionNames = nativeControls.map((control) => normalize(this.getGiftOptionName(control)));
      let matched = null;
      let matchedValues = null;

      for (const cartItem of cart.items || []) {
        const valuesByName = new Map();
        (cartItem.options_with_values || []).forEach((option) => {
          const name = normalize(option.name);
          const value = String(option.value || '').trim();
          if (name && value) valuesByName.set(name, value);
        });
        Object.entries(cartItem.properties || {}).forEach(([name, value]) => {
          const normalizedName = normalize(name);
          if (normalizedName && !normalizedName.startsWith('_') && value && !valuesByName.has(normalizedName)) {
            valuesByName.set(normalizedName, String(value).trim());
          }
        });

        const requestedValues = nativeOptionNames.map((name) => valuesByName.get(name));
        if (requestedValues.some((value) => !value)) continue;

        const candidate = variants.find((variant) => (
          variant.available && requestedValues.every((value, optionIndex) => (
            normalize(variant.options?.[optionIndex]) === normalize(value)
          ))
        ));
        if (candidate) {
          matched = candidate;
          matchedValues = valuesByName;
          break;
        }
      }

      if (!matched) return null;

      // The cart fetch can resolve long after the shopper started choosing.
      // Never overwrite a control they already touched, and never re-seed a
      // card that has been closed in the meantime.
      if (!item.classList.contains('is-variant-open')) return null;

      // The flag lets the change handlers tell prefill's own synthetic events
      // apart from real choices (including sheet taps, which are synthetic
      // too) — without it prefill marked every control touched by itself and
      // could never run again on this page.
      this.giftPrefilling = true;
      try {
        nativeControls.forEach((control, optionIndex) => {
          if (control.dataset.giftTouched === 'true') return;
          const value = matched.options?.[optionIndex];
          if (!value || !Array.from(control.options).some((option) => option.value === value)) return;
          control.value = value;
          control.dispatchEvent(new Event('change', { bubbles: true }));
        });

        requiredControls.forEach((control) => {
          if (control.dataset.giftTouched === 'true') return;
          const value = matchedValues?.get(normalize(this.getGiftOptionName(control)));
          if (!value) return;
          const option = Array.from(control.options).find((candidate) => normalize(candidate.value) === normalize(value));
          if (!option) return;
          control.value = option.value;
          control.dispatchEvent(new Event('change', { bubbles: true }));
        });
      } finally {
        this.giftPrefilling = false;
      }

      this.syncMobilePickerVariant({ item, button }, matched);
      return matched;
    }

    async refreshCartDrawer() {
      const root = window.Shopify?.routes?.root || '/';
      const [cartResponse, sectionsResponse] = await Promise.all([
        fetch(`${root}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } }),
        fetch(`${root}?sections=cart-drawer,cart-icon-bubble`, { credentials: 'same-origin', headers: { Accept: 'application/json' } }),
      ]);
      if (!cartResponse.ok || !sectionsResponse.ok) throw new Error('Cart refresh failed');

      const [cart, sections] = await Promise.all([cartResponse.json(), sectionsResponse.json()]);

      // Re-render the badge from its own section: patching the span text
      // could never create the badge on the first add to an empty cart, and
      // it wrote the raw item count where the header serves the eligible one.
      const bubbleHost = document.getElementById('cart-icon-bubble');
      const bubbleMarkup = sections['cart-icon-bubble'];
      if (bubbleHost && typeof bubbleMarkup === 'string') {
        const template = document.createElement('template');
        template.innerHTML = bubbleMarkup;
        const rendered = template.content.querySelector('#cart-icon-bubble') || template.content.firstElementChild;
        bubbleHost.innerHTML = rendered ? rendered.innerHTML : bubbleMarkup;
      } else {
        document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"], .cart-count-bubble .count').forEach((count) => {
          count.textContent = String(cart.item_count || 0);
        });
      }

      const section = document.getElementById('shopify-section-cart-drawer');
      const markup = sections['cart-drawer'];
      if (section && markup) {
        section.outerHTML = markup;
        const drawer = document.querySelector('#shopify-section-cart-drawer cart-drawer');
        if (drawer) {
          drawer.classList.add('active');
          drawer.open = true;
        }
      }

      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: { source: 'lunicora-product-gift', cart },
      }));
    }

    async addGiftToCart(item, button, variant) {
      const form = this.getGiftForm(item, button);
      const upsell = item.querySelector('[data-product-id]');
      const expectedProductId = String(upsell?.dataset.productId || '');
      if (!variant?.id || (expectedProductId && variant.product_id && String(variant.product_id) !== expectedProductId)) return;

      const properties = {};
      item.querySelectorAll('[name^="properties["]').forEach((control) => {
        if (control.disabled || !control.name || !String(control.value || '').trim()) return;
        const propertyName = control.name.match(/^properties\[([^\]]+)\]$/)?.[1];
        if (propertyName) properties[propertyName] = String(control.value).trim();
      });

      const cartItem = {
        id: Number(variant.id),
        quantity: 1,
      };
      if (Object.keys(properties).length) cartItem.properties = properties;

      const sellingPlan = form?.querySelector('[name="selling_plan"]')?.value;
      if (sellingPlan) cartItem.selling_plan = Number(sellingPlan);

      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.classList.add('loading');

      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items: [cartItem] }),
        });
        if (!response.ok) {
          const error = new Error(`Cart add failed: ${response.status}`);
          error.status = response.status;
          throw error;
        }
        this.closeVariantPickers();

        // The add already succeeded. A failed drawer refresh must not look
        // like a failed add — the shopper would retry and get a duplicate
        // line — so it falls back to a bare cart:update for the listeners.
        try {
          await this.refreshCartDrawer();
        } catch (_refreshError) {
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: { source: 'lunicora-product-gift' },
          }));
        }
      } catch (error) {
        button.disabled = false;
        this.showGiftAddError(button, error?.status === 422 ? 'SOLD OUT' : 'TRY AGAIN');
      } finally {
        button.removeAttribute('aria-busy');
        button.classList.remove('loading');
      }
    }

    showGiftAddError(button, message) {
      const label = button.querySelector('span');
      if (!label) return;
      // The restore value is captured once per error episode: a second failure
      // inside the 2.5s window must not capture the error text itself, and a
      // failure on another button must not cancel this one's restore.
      if (!button.dataset.giftErrorRestore) button.dataset.giftErrorRestore = label.textContent;
      label.textContent = message;
      this.giftErrorTimers = this.giftErrorTimers || new Map();
      clearTimeout(this.giftErrorTimers.get(button));
      this.giftErrorTimers.set(button, setTimeout(() => {
        if (label.textContent === message) label.textContent = button.dataset.giftErrorRestore;
        delete button.dataset.giftErrorRestore;
        this.giftErrorTimers?.delete(button);
      }, 2500));
    }

    updateMobilePickerPreview() {
      const state = this.mobilePickerState;
      const variant = this.getMobilePickerVariant(state);
      if (!state || !variant || !this.mobilePicker) return;

      const image = this.mobilePicker.querySelector('[data-gift-mobile-image]');
      const price = this.mobilePicker.querySelector('[data-gift-mobile-price]');
      const compare = this.mobilePicker.querySelector('[data-gift-mobile-compare]');
      const imageSource = variant.featured_image?.src
        || variant.featured_media?.preview_image?.src
        || state.fallbackImage;

      if (imageSource) {
        image.src = imageSource.startsWith('//') ? `${window.location.protocol}${imageSource}` : imageSource;
      }
      this.syncMobilePickerVariant(state, variant);

      // The single-variant fallback carries only an id, so leave the prices the
      // card already supplied instead of formatting undefined into $0.00.
      if (variant.price == null) return;

      if (price) price.textContent = this.formatMobilePickerMoney(variant.price);
      if (compare) {
        const hasCompare = Number(variant.compare_at_price) > Number(variant.price);
        compare.hidden = !hasCompare;
        compare.textContent = hasCompare ? this.formatMobilePickerMoney(variant.compare_at_price) : '';
      }
    }

    updateInlineGiftPreview(item, variant) {
      if (!item || !variant) return;

      const image = item.querySelector('.upsell__image img');
      const price = item.querySelector('.upsell__price .regular-price');
      const compare = item.querySelector('.upsell__price .compare-price');

      if (image) {
        if (!image.dataset.giftFallbackSrc) {
          image.dataset.giftFallbackSrc = image.currentSrc || image.src || '';
        }

        const imageSource = variant.featured_image?.src
          || variant.featured_media?.preview_image?.src
          || image.dataset.giftFallbackSrc;

        if (imageSource) {
          image.src = imageSource.startsWith('//') ? `${window.location.protocol}${imageSource}` : imageSource;
        }
      }

      // Single-variant fallbacks contain only an id; keep their server-rendered
      // price instead of replacing it with a formatted zero.
      if (variant.price == null) return;

      if (price) price.textContent = this.formatMobilePickerMoney(variant.price);
      if (compare) {
        const hasCompare = Number(variant.compare_at_price) > Number(variant.price);
        compare.classList.toggle('hidden', !hasCompare);
        compare.textContent = hasCompare ? this.formatMobilePickerMoney(variant.compare_at_price) : '';
      }
    }

    openMobilePicker({ item, button, requiredControls }) {
      if (!this.mobilePicker) return;

      const controls = [
        ...item.querySelectorAll('.upsell__variant-picker select, .lunicora-product-gifts__color-picker:not([hidden]) select, [data-gift-custom-options] select'),
      ];
      this.syncMobileNameField(item);
      const optionsContainer = this.mobilePicker.querySelector('[data-gift-mobile-options]');
      const title = this.mobilePicker.querySelector('[data-gift-mobile-title]');
      const image = this.mobilePicker.querySelector('[data-gift-mobile-image]');
      const price = this.mobilePicker.querySelector('[data-gift-mobile-price]');
      const compare = this.mobilePicker.querySelector('[data-gift-mobile-compare]');

      let variants = [];
      try {
        variants = JSON.parse(item.querySelector('.upsell__variant-picker script[type="application/json"]')?.textContent || '[]');
      } catch (_error) {
        variants = [];
      }

      this.mobilePickerState = {
        item,
        button,
        requiredControls,
        controls,
        nativeControls: [...item.querySelectorAll('.upsell__variant-picker select')],
        variants,
        fallbackImage: item.querySelector('.upsell__image img')?.currentSrc || item.querySelector('.upsell__image img')?.src || '',
      };
      title.textContent = item.querySelector('.upsell__title h3')?.textContent?.trim() || 'Product options';
      image.src = this.mobilePickerState.fallbackImage;
      image.alt = title.textContent;
      price.textContent = item.querySelector('.regular-price')?.textContent?.trim() || '';
      const compareText = item.querySelector('.compare-price')?.textContent?.trim() || '';
      compare.hidden = !compareText;
      compare.textContent = compareText;
      optionsContainer.replaceChildren();

      controls.forEach((control, index) => {
        const group = document.createElement('fieldset');
        group.className = 'lunicora-variant-picker__option';
        const optionName = (control.name.match(/\[([^\]]+)\]/)?.[1] || control.getAttribute('aria-label') || 'Option').trim();
        const legend = document.createElement('legend');
        legend.textContent = optionName;
        group.appendChild(legend);

        const values = document.createElement('div');
        values.className = 'lunicora-variant-picker__values lunicora-product-gifts-picker__values';
        const trigger = document.createElement('button');
        trigger.type = 'button';
        trigger.className = 'lunicora-product-gifts-picker__trigger';
        trigger.dataset.giftMobileTrigger = String(index);
        trigger.setAttribute('aria-expanded', 'false');
        trigger.setAttribute('aria-controls', `LunicoraGiftMobileMenu-${index}`);
        values.appendChild(trigger);

        const menu = document.createElement('div');
        menu.className = 'lunicora-product-gifts-picker__menu';
        menu.id = `LunicoraGiftMobileMenu-${index}`;
        menu.dataset.giftMobileMenu = String(index);
        menu.hidden = true;
        values.appendChild(menu);
        group.appendChild(values);
        optionsContainer.appendChild(group);
      });

      // Text options (an Easify text box) get a field in the sheet that writes
      // straight through to the card's own input, like the name box does.
      [...item.querySelectorAll('[data-gift-custom-text]')].forEach((source, index) => {
        const field = document.createElement('div');
        field.className = 'lunicora-variant-picker__name-field lunicora-product-gifts-picker__text-field';
        const label = document.createElement('label');
        label.setAttribute('for', `LunicoraGiftMobileText-${index}`);
        label.textContent = source.dataset.giftCustomLabel || 'Option';
        const hint = document.createElement('span');
        hint.textContent = source.hasAttribute('data-gift-required-option') ? '*' : '(Optional)';
        label.append(' ', hint);
        const wrap = document.createElement('div');
        wrap.className = 'lunicora-variant-picker__name-input-wrap';
        const input = document.createElement('input');
        input.id = `LunicoraGiftMobileText-${index}`;
        input.type = 'text';
        input.autocomplete = 'off';
        input.placeholder = source.placeholder || '';
        if (source.maxLength > 0) input.maxLength = source.maxLength;
        input.value = source.value || '';
        input.dataset.giftMobileText = String(index);
        input.addEventListener('input', () => {
          source.value = input.value;
          source.dispatchEvent(new Event('input', { bubbles: true }));
          this.refreshMobileSubmit();
        });
        wrap.appendChild(input);
        field.append(label, wrap);
        optionsContainer.appendChild(field);
      });

      this.mobilePicker.hidden = false;
      document.documentElement.classList.add('lunicora-variant-picker-open');
      this.syncMobilePicker();
      requestAnimationFrame(() => this.mobilePicker.querySelector('[data-gift-mobile-close]')?.focus());
    }

    syncMobilePicker() {
      const state = this.mobilePickerState;
      if (!state || !this.mobilePicker) return;

      this.mobilePicker.querySelectorAll('[data-gift-mobile-menu]').forEach((menu) => {
        const index = Number(menu.dataset.giftMobileMenu);
        const original = state.controls[index];
        if (!original) return;
        const trigger = this.mobilePicker.querySelector(`[data-gift-mobile-trigger="${index}"]`);
        const selectedOption = original.selectedOptions?.[0];
        trigger.textContent = selectedOption?.textContent?.trim() || `Choose ${original.getAttribute('aria-label') || 'option'}`;
        menu.replaceChildren();

        Array.from(original.options).forEach((option) => {
          if (!option.value || option.hidden) return;
          const optionButton = document.createElement('button');
          optionButton.type = 'button';
          optionButton.className = 'lunicora-product-gifts-picker__option';
          optionButton.dataset.giftMobileOption = String(index);
          optionButton.value = option.value;
          optionButton.textContent = option.textContent.trim();
          optionButton.disabled = option.disabled;
          optionButton.setAttribute('aria-pressed', String(option.value === original.value));
          menu.appendChild(optionButton);
        });
        menu.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
      });

      const complete = this.isMobilePickerComplete(state);
      const submit = this.mobilePicker.querySelector('[data-gift-mobile-submit]');
      const status = this.mobilePicker.querySelector('[data-gift-mobile-status]');
      this.updateMobilePickerPreview();
      if (submit) submit.disabled = !complete;
      if (status) status.textContent = complete ? 'In stock' : 'Choose all options';
    }

    closeMobilePicker({ returnFocus = true, keepVariantState = false } = {}) {
      const state = this.mobilePickerState;
      if (!state || !this.mobilePicker) return;
      this.mobilePicker.hidden = true;
      document.documentElement.classList.remove('lunicora-variant-picker-open');
      this.mobilePickerState = null;
      if (!keepVariantState) this.closeVariantPickers();
      if (returnFocus && state.button?.isConnected) state.button.focus();
    }

    /*
      Build, for each gift, the same app options the shopper sees on that
      product's own page: every Easify option a shopper can fill in, with the
      product page's required flag (required stays required, optional stays
      visible but optional). Choice options become a dropdown, text options a
      text box. Runs before setupVariantPickers so the required list sees the
      final controls. Without a matching option set the pre-rendered handle
      fallback is kept; a hidden fallback is removed so the theme's
      open-state CSS cannot reveal it.
    */
    applyCustomOptions() {
      this.querySelectorAll('.lunicora-product-gifts__item').forEach((item) => {
        const fallback = item.querySelector('[data-gift-color-picker]');
        const container = item.querySelector('[data-gift-custom-options]');
        const dataElement = item.querySelector('[data-gift-product-json]');
        if (!dataElement || !container) return;

        let product;
        try {
          product = JSON.parse(dataElement.textContent);
        } catch (_error) {
          return;
        }

        const options = getEasifyCustomOptions(product);
        if (!options.length) {
          if (fallback && fallback.hidden) fallback.remove();
          container.remove();
          return;
        }

        const selectWrapClass = fallback?.querySelector('.upsell__select')?.className || 'upsell__select select select--small no-background';
        const caret = fallback?.querySelector('.upsell__select svg') || null;
        if (fallback) fallback.remove();
        container.replaceChildren();

        options.forEach((option, index) => {
          const id = `LunicoraGiftOption-${product.id}-${index}`;
          const label = document.createElement('label');
          label.className = 'visually-hidden';
          label.setAttribute('for', id);
          label.textContent = option.name;

          if (option.kind === 'text') {
            const field = document.createElement('div');
            field.className = 'lunicora-product-gifts__name-field lunicora-product-gifts__custom-text';
            const input = document.createElement('input');
            input.id = id;
            input.type = 'text';
            input.className = 'lunicora-product-gifts__name-input';
            input.name = `properties[${option.key}]`;
            input.placeholder = `${option.placeholder || option.name}${option.required ? ' *' : ''}`;
            input.autocomplete = 'off';
            input.autocapitalize = 'off';
            if (option.maxLength) input.maxLength = option.maxLength;
            input.dataset.giftCustomText = '';
            input.dataset.giftCustomLabel = option.name;
            if (option.required) {
              input.setAttribute('data-gift-required-option', '');
              input.required = true;
            }
            field.append(label, input);
            container.appendChild(field);
            return;
          }

          const wrapper = document.createElement('div');
          wrapper.className = 'lunicora-product-gifts__color-picker lunicora-product-gifts__custom-choice';
          const selectWrap = document.createElement('div');
          selectWrap.className = selectWrapClass;
          const select = document.createElement('select');
          select.id = id;
          select.className = 'select__select lunicora-product-gifts__required-select';
          select.name = `properties[${option.key}]`;
          select.setAttribute('aria-label', option.name);
          select.dataset.giftCustomChoice = '';
          const placeholder = document.createElement('option');
          placeholder.value = '';
          placeholder.selected = true;
          placeholder.textContent = `Choose ${option.name}${option.required ? ' *' : ''}`;
          select.appendChild(placeholder);
          option.values.forEach((value) => {
            const element = document.createElement('option');
            element.value = value.label;
            element.textContent = value.label;
            select.appendChild(element);
          });
          const preselected = option.values.find((value) => value.isDefault);
          if (preselected) select.value = preselected.label;
          if (option.required) {
            select.setAttribute('data-gift-required-option', '');
            select.required = true;
          }
          selectWrap.appendChild(select);
          if (caret) selectWrap.appendChild(caret.cloneNode(true));
          wrapper.append(label, selectWrap);
          container.appendChild(wrapper);
        });

        container.hidden = false;
      });
    }

    /*
      Customily products carry their personalizer on their own page only, so
      the gift becomes a hand-off: ADD+ turns into CUSTOMIZE and every click on
      the card opens the product page instead of adding an unfinished line.
    */
    checkPersonalizers() {
      this.querySelectorAll('.lunicora-product-gifts__item').forEach((item) => {
        const dataElement = item.querySelector('[data-gift-product-json]');
        if (!dataElement) return;
        let product;
        try {
          product = JSON.parse(dataElement.textContent);
        } catch (_error) {
          return;
        }
        hasExternalPersonalizer(product).then((personalized) => {
          if (!personalized || !item.isConnected) return;
          item.dataset.giftPersonalizer = 'customily';
          if (!item.dataset.giftProductUrl && product.url) item.dataset.giftProductUrl = product.url;
          const label = item.querySelector('.upsell__add-btn span');
          if (label) label.textContent = 'CUSTOMIZE';
        });
      });
    }

    bindRequiredControls(button, requiredControls) {
      requiredControls.forEach((control) => {
        if (this.requiredOptionHandlers.has(control)) return;
        const requiredHandler = () => {
          if (!this.giftPrefilling) control.dataset.giftTouched = 'true';
          this.updateRequiredState(button, requiredControls);
        };
        control.addEventListener('change', requiredHandler);
        control.addEventListener('input', requiredHandler);
        this.requiredOptionHandlers.set(control, requiredHandler);
      });
    }

    refreshMobileSubmit() {
      const state = this.mobilePickerState;
      if (!state || !this.mobilePicker) return;
      const complete = this.isMobilePickerComplete(state);
      const submit = this.mobilePicker.querySelector('[data-gift-mobile-submit]');
      const status = this.mobilePicker.querySelector('[data-gift-mobile-status]');
      if (submit) submit.disabled = !complete;
      if (status) status.textContent = complete ? 'In stock' : 'Choose all options';
    }

    /*
      Gift image and title open the same picker as ADD+ (block setting
      "Open the gift options"), so the shopper never leaves the page. Modified
      clicks (new tab, etc.) and single-variant gifts without a name box keep
      the plain link, because for those there is nothing to pick.
    */
    setupPickerLinks() {
      this.onPickerLinkClick = (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button === 1) return;

        const link = event.target.closest('[data-gift-open-picker]');
        if (!link || !this.contains(link)) return;

        const item = link.closest('.lunicora-product-gifts__item');
        const button = item?.querySelector('.upsell__add-btn');
        if (!item || !button) return;
        if (item.dataset.giftPersonalizer) return;
        const hasPicker = !!item.querySelector('.upsell__variant-picker');
        const hasName = !!item.querySelector('[data-gift-name-input]');
        if (!hasPicker && !hasName) return;

        event.preventDefault();
        if (item.classList.contains('is-variant-open')) {
          if (window.matchMedia('(max-width: 749px)').matches && this.mobilePicker?.hidden) {
            this.openMobilePicker({ item, button, requiredControls: [...item.querySelectorAll('[data-gift-required-option]')] });
          }
          return;
        }
        this.stopAutoplay?.();
        button.click();
      };
      this.addEventListener('click', this.onPickerLinkClick);
    }

    setupVariantPickers() {
      this.querySelectorAll('.lunicora-product-gifts__item').forEach((item, index) => {
        const picker = item.querySelector('.upsell__variant-picker');
        const button = item.querySelector('.upsell__add-btn');
        const requiredControls = [...item.querySelectorAll('[data-gift-required-option]')];

        if (!button) return;

        const nameInput = item.querySelector('[data-gift-name-input]');
        if (nameInput) {
          const nameHandler = () => {
            this.updateGiftNameCount(nameInput);
            if (this.mobilePickerState?.item === item) {
              const mobileName = this.mobilePicker?.querySelector('[data-gift-mobile-name]');
              if (mobileName && mobileName.value !== nameInput.value) {
                mobileName.value = nameInput.value;
                this.updateMobileNameCount();
              }
            }
          };
          nameInput.addEventListener('input', nameHandler);
          this.nameInputHandlers.set(nameInput, nameHandler);
          this.updateGiftNameCount(nameInput);
        }

        if (!picker) {
          const handler = (event) => {
            event.preventDefault();
            event.stopImmediatePropagation();
            if (item.dataset.giftPersonalizer) {
              if (item.dataset.giftProductUrl) window.location.assign(item.dataset.giftProductUrl);
              return;
            }

            // A gift with a single variant has nothing to choose, but it still
            // opens once when it offers a name box, so the shopper gets a chance
            // to type one. The second click is the real add.
            if (nameInput && !item.classList.contains('is-variant-open')) {
              this.closeVariantPickers(item);
              item.classList.add('is-variant-open');
              button.setAttribute('aria-expanded', 'true');

              const openLabel = button.querySelector('span');
              if (openLabel) {
                openLabel.dataset.giftOriginalLabel = openLabel.innerHTML;
                openLabel.textContent = 'ADD TO CART';
              }

              if (window.matchMedia('(max-width: 749px)').matches) {
                this.openMobilePicker({ item, button, requiredControls });
              }

              requestAnimationFrame(() => this.updateButtons());
              return;
            }

            const isComplete = requiredControls.every((control) => String(control.value || '').trim() !== '');
            if (!isComplete) {
              this.updateRequiredState(button, requiredControls);
              requiredControls.find((control) => !String(control.value || '').trim())?.focus();
              return;
            }

            const selectedVariant = this.getDefaultGiftVariant(item, button);
            if (selectedVariant) void this.addGiftToCart(item, button, selectedVariant);
          };
          button.addEventListener('click', handler, true);
          this.variantButtonHandlers.set(button, handler);
          this.bindRequiredControls(button, requiredControls);
          return;
        }

        if (!picker.id) picker.id = `LunicoraGiftOptions-${index}-${Date.now()}`;
        picker.hidden = true;
        button.setAttribute('aria-controls', picker.id);
        button.setAttribute('aria-expanded', 'false');

        const syncInlineVariant = () => {
          let variants = [];
          try {
            variants = JSON.parse(picker.querySelector('script[type="application/json"]')?.textContent || '[]');
          } catch (_error) {
            variants = [];
          }

          const selections = [...picker.querySelectorAll('select')].map((control) => control.value);
          const variant = variants.find((candidate) => (
            candidate.available && selections.every((value, optionIndex) => candidate.options?.[optionIndex] === value)
          )) || null;

          if (variant) {
            this.syncMobilePickerVariant({ item, button }, variant);
            this.updateInlineGiftPreview(item, variant);
          }
          return variant;
        };

        picker.querySelectorAll('select').forEach((control) => {
          control.addEventListener('change', () => {
            if (!this.giftPrefilling) control.dataset.giftTouched = 'true';
            requestAnimationFrame(syncInlineVariant);
          });
        });
        syncInlineVariant();

        const handler = (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();
          if (item.dataset.giftPersonalizer) {
            if (item.dataset.giftProductUrl) window.location.assign(item.dataset.giftProductUrl);
            return;
          }

          if (item.classList.contains('is-variant-open')) {
            const selectedVariant = syncInlineVariant();
            const isComplete = requiredControls.every(
              (control) => String(control.value || '').trim() !== '',
            );

            if (!isComplete || !selectedVariant) {
              this.updateRequiredState(button, requiredControls);
              if (!isComplete) {
                requiredControls.find((control) => !String(control.value || '').trim())?.focus();
              }
              return;
            }

            void this.addGiftToCart(item, button, selectedVariant);
            return;
          }

          this.closeVariantPickers(item);

          item.classList.add('is-variant-open');
          picker.hidden = false;
          button.setAttribute('aria-expanded', 'true');

          const label = button.querySelector('span');
          if (label) {
            label.dataset.giftOriginalLabel = label.innerHTML;
            label.textContent = 'ADD TO CART';
          }

          this.updateRequiredState(button, requiredControls);

          if (window.matchMedia('(max-width: 749px)').matches) {
            this.openMobilePicker({ item, button, requiredControls });
          }

          void this.prefillGiftVariantFromCart(item, picker, button, requiredControls).then(() => {
            syncInlineVariant();
            this.updateRequiredState(button, requiredControls);
            if (this.mobilePickerState?.item === item) this.syncMobilePicker();
          });

          requestAnimationFrame(() => this.updateButtons());
        };

        button.addEventListener('click', handler, true);
        this.variantButtonHandlers.set(button, handler);
        this.bindRequiredControls(button, requiredControls);

      });
    }

    updateRequiredState(button, controls) {
      const isComplete = controls.every((control) => String(control.value || '').trim() !== '');
      button.disabled = !isComplete;
      button.setAttribute('aria-disabled', String(!isComplete));
    }

    closeVariantPickers(exception) {
      this.querySelectorAll('.lunicora-product-gifts__item.is-variant-open').forEach((item) => {
        if (item === exception) return;

        const picker = item.querySelector('.upsell__variant-picker');
        const button = item.querySelector('.upsell__add-btn');
        const label = button?.querySelector('span');

        item.classList.remove('is-variant-open');
        if (picker) picker.hidden = true;
        if (button) {
          button.disabled = false;
          button.setAttribute('aria-expanded', 'false');
          button.setAttribute('aria-disabled', 'false');
        }

        // Selections survive the close on purpose: wiping only the required
        // colour while the size and typed name stayed put threw away exactly
        // the field that gates the add button.

        if (label?.dataset.giftOriginalLabel) {
          label.innerHTML = label.dataset.giftOriginalLabel;
          delete label.dataset.giftOriginalLabel;
        }
      });
    }

    setupAutoplay() {
      // Auto-advance the gift columns while the block is on screen. Driven by
      // the block settings "Auto-scroll gift columns" / "Auto-scroll every"
      // (data-gift-autoplay / data-gift-autoplay-interval on this element).
      if (this.dataset.giftAutoplay !== 'true' || !this.track) return;

      const seconds = Number.parseFloat(this.dataset.giftAutoplayInterval);
      this.autoplayDelay = Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 4000;
      this.autoplayStopped = false;
      this.autoplayVisible = false;
      this.autoplayTimer = null;

      // The first time the customer touches the block it stops for good: a tap,
      // click, key press, keyboard focus, a real mouse movement over it or a
      // horizontal wheel/trackpad swipe. A vertical wheel is just the page
      // scrolling past, and the synthetic mousemove Chrome fires after a page
      // scroll carries no movement, so neither of those counts.
      this.onAutoplayInteract = () => this.stopAutoplay();
      this.onAutoplayMouseMove = (event) => {
        if (event.movementX !== 0 || event.movementY !== 0) this.stopAutoplay();
      };
      this.onAutoplayWheel = (event) => {
        if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) this.stopAutoplay();
      };
      this.onAutoplayVisibility = () => {
        if (document.hidden) this.pauseAutoplay();
        else this.resumeAutoplay();
      };

      this.autoplayInteractEvents = ['pointerdown', 'touchstart', 'click', 'keydown', 'focusin'];
      this.autoplayInteractEvents.forEach((type) => {
        this.addEventListener(type, this.onAutoplayInteract, { capture: true, passive: true });
      });
      this.addEventListener('mousemove', this.onAutoplayMouseMove, { capture: true, passive: true });
      this.addEventListener('wheel', this.onAutoplayWheel, { capture: true, passive: true });
      document.addEventListener('visibilitychange', this.onAutoplayVisibility);

      if ('IntersectionObserver' in window) {
        this.autoplayObserver = new IntersectionObserver((entries) => {
          const entry = entries[entries.length - 1];
          this.autoplayVisible = Boolean(entry && entry.isIntersecting && entry.intersectionRatio >= 0.35);
          if (this.autoplayVisible) this.resumeAutoplay();
          else this.pauseAutoplay();
        }, { threshold: [0, 0.35, 0.7] });
        this.autoplayObserver.observe(this);
      } else {
        this.autoplayVisible = true;
        this.resumeAutoplay();
      }
    }

    resumeAutoplay() {
      if (this.autoplayStopped || !this.autoplayVisible || this.autoplayTimer) return;
      if (document.hidden) return;
      this.autoplayTimer = window.setInterval(() => this.autoplayTick(), this.autoplayDelay);
    }

    pauseAutoplay() {
      if (!this.autoplayTimer) return;
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    stopAutoplay() {
      this.autoplayStopped = true;
      this.pauseAutoplay();
      this.autoplayObserver?.disconnect();
      this.autoplayObserver = null;
    }

    autoplayTick() {
      if (this.autoplayStopped || !this.autoplayVisible || document.hidden) {
        this.pauseAutoplay();
        return;
      }

      const tolerance = 3;
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      if (maxScroll <= tolerance) return;

      if (this.track.scrollLeft >= maxScroll - tolerance) {
        // Still on the last column: the post-slide rotation could not run (item
        // count is not a whole number of columns, or the tab was hidden). Rotate
        // now and give the browser a couple of frames to settle the reorder
        // before sliding on, otherwise scroll snapping cancels the slide. If
        // rotating is impossible, jump straight back to the start instead of
        // animating backwards.
        if (!this.rotateFirstColumnToEnd()) {
          this.scrollTrackInstantly(0);
          return;
        }
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            if (!this.autoplayStopped) this.move(1);
          });
        });
        return;
      }

      this.move(1);
      this.scheduleEndRotation();
    }

    scheduleEndRotation() {
      // Once the slide has finished, if it landed on the last column, move the
      // first column behind it straight away. Nothing visible changes, and the
      // next tick is then a plain forward slide, so the carousel never pauses
      // or scrolls backwards. scrollend is used where supported, with a timeout
      // as the fallback.
      this.cancelEndRotation();
      const settle = () => {
        this.cancelEndRotation();
        if (this.autoplayStopped || !this.track) return;
        const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        if (maxScroll > 3 && this.track.scrollLeft >= maxScroll - 3) this.rotateFirstColumnToEnd();
      };
      this.onAutoplayScrollEnd = settle;
      this.track.addEventListener('scrollend', settle);
      this.autoplayRotateTimer = window.setTimeout(settle, Math.min(900, Math.max(300, this.autoplayDelay - 300)));
    }

    cancelEndRotation() {
      if (this.onAutoplayScrollEnd) {
        this.track.removeEventListener('scrollend', this.onAutoplayScrollEnd);
        this.onAutoplayScrollEnd = null;
      }
      if (this.autoplayRotateTimer) {
        window.clearTimeout(this.autoplayRotateTimer);
        this.autoplayRotateTimer = null;
      }
    }

    getTrackRows() {
      const rows = getComputedStyle(this.track).gridTemplateRows.split(' ').filter(Boolean).length;
      return rows > 0 ? rows : 1;
    }

    rotateFirstColumnToEnd() {
      // Keeps the carousel moving forward forever: the first column is moved
      // behind the last one and the scroll position is corrected so nothing on
      // screen changes, then the regular move(1) slides on to it. Nodes are
      // moved, not cloned, so every add button keeps its handlers.
      const items = [...this.track.children].filter((node) => node.classList.contains('lunicora-product-gifts__item'));
      const rows = this.getTrackRows();
      if (items.length < rows * 2 || items.length % rows !== 0) return false;

      const trackStyles = getComputedStyle(this.track);
      const itemGap = Number.parseFloat(trackStyles.columnGap) || Number.parseFloat(trackStyles.gap) || 0;
      const columnStep = items[0].getBoundingClientRect().width + itemGap;
      // Work out the corrected position before touching the DOM: newer Chrome
      // re-snaps to the previously snapped item as soon as layout runs, so a
      // scrollLeft read after the move would already be corrected and we would
      // subtract a column twice.
      const target = Math.max(0, this.track.scrollLeft - columnStep);

      const fragment = document.createDocumentFragment();
      items.slice(0, rows).forEach((item) => fragment.appendChild(item));
      this.track.appendChild(fragment);
      this.scrollTrackInstantly(target);
      return true;
    }

    scrollTrackInstantly(left) {
      // The track has CSS scroll-behavior: smooth, which would animate even a
      // plain scrollLeft assignment.
      const previous = this.track.style.scrollBehavior;
      this.track.style.scrollBehavior = 'auto';
      this.track.scrollLeft = left;
      this.track.style.scrollBehavior = previous;
    }

    teardownAutoplay() {
      this.pauseAutoplay();
      if (this.track) this.cancelEndRotation();
      this.autoplayObserver?.disconnect();
      this.autoplayObserver = null;
      this.autoplayInteractEvents?.forEach((type) => this.removeEventListener(type, this.onAutoplayInteract, { capture: true }));
      if (this.onAutoplayMouseMove) this.removeEventListener('mousemove', this.onAutoplayMouseMove, { capture: true });
      if (this.onAutoplayWheel) this.removeEventListener('wheel', this.onAutoplayWheel, { capture: true });
      if (this.onAutoplayVisibility) document.removeEventListener('visibilitychange', this.onAutoplayVisibility);
    }

    // One dot per column under the carousel; the active one follows the scroll
    // position, so it stays right after the autoplay rotation as well.
    updateDots() {
      const host = this.querySelector('[data-gift-dots]');
      if (!host || !this.track) return;
      const items = [...this.track.children].filter((node) => node.classList.contains('lunicora-product-gifts__item'));
      const rows = this.getTrackRows();
      const columns = Math.max(1, Math.ceil(items.length / rows));
      const trackStyles = getComputedStyle(this.track);
      const gap = Number.parseFloat(trackStyles.columnGap) || Number.parseFloat(trackStyles.gap) || 0;
      const step = (items[0] ? items[0].getBoundingClientRect().width : this.track.clientWidth) + gap;
      const active = step > 0 ? Math.min(columns - 1, Math.max(0, Math.round(this.track.scrollLeft / step))) : 0;
      if (host.childElementCount !== columns) {
        host.innerHTML = '';
        for (let index = 0; index < columns; index += 1) {
          const dot = document.createElement('span');
          dot.className = 'lunicora-product-gifts__dot';
          host.appendChild(dot);
        }
      }
      host.hidden = columns < 2;
      [...host.children].forEach((dot, index) => dot.classList.toggle('is-active', index === active));
    }

    updateButtons() {
      if (!this.track || !this.previousButton || !this.nextButton) return;

      const tolerance = 3;
      const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const atStart = this.track.scrollLeft <= tolerance;
      const atEnd = this.track.scrollLeft >= maxScroll - tolerance;

      this.previousButton.disabled = atStart;
      this.nextButton.disabled = atEnd;
      this.previousButton.setAttribute('aria-disabled', String(atStart));
      this.nextButton.setAttribute('aria-disabled', String(atEnd));
    }
  }

  customElements.define('lunicora-product-gifts-controller', LunicoraProductGiftsController);
}
